import { HttpClient } from '@angular/common/http';
import { DOCUMENT, Injectable, inject, signal } from '@angular/core';

/**
 * Browser context stored with a TCPA consent (`tcpa.browserMetadata` on `POST /customer-intake`).
 *
 * The first seven keys, and their values, match dot-com's `consents.browser_metadata` JSONB
 * (app/models/consent.rb, filled server-side by the `browser` gem 6.2.0 from the User-Agent, and
 * `REMOTE_ADDR` for `ip_address`). The backend stores this as sent, so `ip_address` is the public IP
 * the client sees, looked up the way kinpub-ui does. The rest is extra context dot-com doesn't record.
 */
export interface TcpaBrowserMetadata {
  /** "Chrome", "Safari", "Firefox", "Microsoft Edge", "Opera", "Samsung Browser" or "Unknown Browser". */
  browser: string;
  browser_version: string;
  /** "macOS", "Windows", "iOS (iPhone)", "Android", "Chrome OS", "Generic Linux" or "Unknown". */
  platform: string;
  platform_version: string;
  /** "iPhone", "iPad", "iPod Touch", "Android" or "Unknown" (desktops are "Unknown", as in dot-com). */
  device_type: string;
  /** The client's public IP from ipify; null until it answers, or if it fails or is blocked. */
  ip_address: string | null;
  page_url: string;
  user_agent: string;
  language: string;
  timezone: string;
  screen: { width: number; height: number };
  referrer: string | null;
  captured_at: string;
}

/** Chromium's User-Agent Client Hints; not in TypeScript's DOM lib yet. */
interface UserAgentData {
  getHighEntropyValues(hints: string[]): Promise<{ platformVersion?: string; fullVersionList?: { brand: string; version: string }[] }>;
}

/** Public-IP lookup kinpub-ui uses (src/app/utils/getClientIpHelper.ts). */
export const CLIENT_IP_URL = 'https://api.ipify.org?format=json';

const BROWSERS: [name: string, version: RegExp][] = [
  ['Microsoft Edge', /Edg(?:e|A|iOS)?\/([\d.]+)/],
  ['Opera', /(?:OPR|OPT)\/([\d.]+)/],
  ['Samsung Browser', /SamsungBrowser\/([\d.]+)/],
  ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
  ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/],
  ['Safari', /Version\/([\d.]+).*Safari/],
];

/**
 * Captures the TCPA browser metadata when /signup loads (SignupComponent's constructor), so consent
 * submits send what the browser looked like when the page was opened, without waiting on anything.
 * Chromium browsers then refine the versions from Client Hints, since their User-Agent is frozen
 * (macOS always reads 10.15.7, for example), and the client IP fills in when ipify answers.
 */
@Injectable({ providedIn: 'root' })
export class TcpaBrowserMetadataService {
  private document = inject(DOCUMENT);
  private http = inject(HttpClient);
  private readonly captured = signal<TcpaBrowserMetadata | null>(null);
  readonly metadata = this.captured.asReadonly();

  /** Takes the snapshot once per page load; later calls do nothing. */
  capture(): void {
    if (this.captured()) return;
    this.captured.set(this.read());
    this.refineFromClientHints();
    this.lookUpClientIp();
  }

  /** The page-load snapshot, or one taken now if `capture()` never ran. */
  snapshot(): TcpaBrowserMetadata {
    this.capture();
    return this.captured()!;
  }

  private read(): TcpaBrowserMetadata {
    const view = this.document.defaultView;
    const ua = view?.navigator.userAgent ?? '';
    const [browser, browser_version] = parseBrowser(ua);
    const [platform, platform_version, device_type] = parsePlatform(ua, view?.navigator.maxTouchPoints ?? 0);
    return {
      browser, browser_version, platform, platform_version, device_type,
      ip_address: null,
      page_url: view?.location.href ?? '',
      user_agent: ua,
      language: view?.navigator.language ?? '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: { width: view?.screen.width ?? 0, height: view?.screen.height ?? 0 },
      referrer: this.document.referrer || null,
      captured_at: new Date().toISOString(),
    };
  }

  private refineFromClientHints(): void {
    const hints = (this.document.defaultView?.navigator as { userAgentData?: UserAgentData } | undefined)?.userAgentData;
    hints?.getHighEntropyValues(['platformVersion', 'fullVersionList'])
      .then(({ platformVersion, fullVersionList }) => this.captured.update(m => m && {
        ...m,
        platform_version: platformVersion || m.platform_version,
        browser_version: fullVersionList?.find(b => b.brand === m.browser || (m.browser === 'Chrome' && b.brand === 'Google Chrome'))?.version
          ?? m.browser_version,
      }))
      .catch(() => undefined);
  }

  private lookUpClientIp(): void {
    this.http.get<{ ip?: string }>(CLIENT_IP_URL).subscribe({
      next: ({ ip }) => ip && this.captured.update(m => m && { ...m, ip_address: ip }),
      error: () => undefined,
    });
  }
}

function parseBrowser(ua: string): [name: string, version: string] {
  for (const [name, pattern] of BROWSERS) {
    const match = ua.match(pattern);
    if (match) return [name, match[1]];
  }
  return ['Unknown Browser', '0'];
}

function parsePlatform(ua: string, maxTouchPoints: number): [platform: string, version: string, device: string] {
  const ios = ua.match(/(iPhone|iPad|iPod).*?OS (\d+(?:_\d+)*)/);
  if (ios) {
    const device = ios[1] === 'iPod' ? 'iPod Touch' : ios[1];
    return [`iOS (${ios[1]})`, ios[2].replaceAll('_', '.'), device];
  }
  // iPadOS asks for the desktop site by default, so it reads as a Mac; only the touch screen gives it away
  const mac = ua.match(/Mac OS X ?([\d_.]+)?/);
  if (mac && maxTouchPoints > 1) return ['iOS (iPad)', (mac[1] ?? '0').replaceAll('_', '.'), 'iPad'];
  const android = ua.match(/Android ?([\d.]+)?/);
  if (android) return ['Android', android[1] ?? '0', 'Android'];
  if (mac) return ['macOS', (mac[1] ?? '0').replaceAll('_', '.'), 'Unknown'];
  const windows = ua.match(/Windows NT ([\d.]+)/);
  if (windows) return ['Windows', windows[1], 'Unknown'];
  if (/CrOS/.test(ua)) return ['Chrome OS', ua.match(/CrOS \S+ ([\d.]+)/)?.[1] ?? '0', 'Unknown'];
  if (/Linux/.test(ua)) return ['Generic Linux', '0', 'Unknown'];
  return ['Unknown', '0', 'Unknown'];
}
